import React, { useMemo, useState, useCallback } from "react";
import type { VennDiagramData } from "@/lib/chat/parseVennDiagramBlock";

interface VennDiagramCanvasProps {
  data: VennDiagramData;
  height?: number | "fill";
  mode?: "inline" | "expanded";
}

interface RegionInfo {
  key: string;
  sets: string[];
  items: string[];
  cx: number;
  cy: number;
}

function getRegionKey(sets: string[]): string {
  return [...sets].sort().join("∩");
}

function computeRegions(data: VennDiagramData): RegionInfo[] {
  const circleIds = data.circles.map((c) => c.id);
  const n = circleIds.length;

  // Generate all possible non-empty subsets of circleIds
  const subsets: string[][] = [];
  for (let mask = 1; mask < 1 << n; mask++) {
    const subset: string[] = [];
    for (let i = 0; i < n; i++) {
      if (mask & (1 << i)) subset.push(circleIds[i]);
    }
    subsets.push(subset);
  }

  // Group intersections by their exact set combination
  const regionMap = new Map<string, string[]>();
  for (const subset of subsets) {
    regionMap.set(getRegionKey(subset), []);
  }

  for (const inter of data.intersections) {
    const key = getRegionKey(inter.sets);
    const existing = regionMap.get(key);
    if (existing) {
      existing.push(inter.label);
    }
  }

  return subsets.map((subset) => {
    const key = getRegionKey(subset);
    return {
      key,
      sets: subset,
      items: regionMap.get(key) || [],
      cx: 0,
      cy: 0,
    };
  });
}

// Circle layout positions (relative to SVG viewBox 0 0 500 320)
function getCirclePositions(count: number) {
  if (count === 2) {
    return [
      { cx: 200, cy: 160, r: 100 },
      { cx: 300, cy: 160, r: 100 },
    ];
  }
  // 3 circles: triangular — centered in wider viewBox
  return [
    { cx: 210, cy: 135, r: 72 },
    { cx: 290, cy: 135, r: 72 },
    { cx: 250, cy: 200, r: 72 },
  ];
}

// Region label positions for count badges
function getRegionPositions(circleIds: string[], count: number) {
  const positions: Record<string, { x: number; y: number }> = {};

  if (count === 2) {
    const [a, b] = circleIds;
    positions[a] = { x: 165, y: 160 };
    positions[b] = { x: 335, y: 160 };
    positions[getRegionKey([a, b])] = { x: 250, y: 160 };
  } else {
    const [a, b, c] = circleIds;
    positions[a] = { x: 175, y: 115 };
    positions[b] = { x: 325, y: 115 };
    positions[c] = { x: 250, y: 240 };
    positions[getRegionKey([a, b])] = { x: 250, y: 118 };
    positions[getRegionKey([a, c])] = { x: 210, y: 190 };
    positions[getRegionKey([b, c])] = { x: 290, y: 190 };
    positions[getRegionKey([a, b, c])] = { x: 250, y: 165 };
  }

  return positions;
}

export const VennDiagramCanvas: React.FC<VennDiagramCanvasProps> = ({
  data,
  height = 300,
  mode = "inline",
}) => {
  const isFill = height === "fill";
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);

  const circlePositions = useMemo(() => getCirclePositions(data.circles.length), [data.circles.length]);
  const regions = useMemo(() => computeRegions(data), [data]);
  const regionPositions = useMemo(
    () => getRegionPositions(data.circles.map((c) => c.id), data.circles.length),
    [data.circles]
  );

  const handleRegionHover = useCallback(
    (key: string | null, e?: React.MouseEvent<SVGElement>) => {
      setHoveredRegion(key);
      if (key && e) {
        const svg = (e.target as SVGElement).closest("svg");
        if (svg) {
          const rect = svg.getBoundingClientRect();
          setTooltipPos({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top - 10,
          });
        }
      } else {
        setTooltipPos(null);
      }
    },
    []
  );

  const hoveredItems = useMemo(() => {
    if (!hoveredRegion) return [];
    return regions.find((r) => r.key === hoveredRegion)?.items || [];
  }, [hoveredRegion, regions]);

  return (
    <div
      className={`border-t border-border p-4 ${isFill ? "flex flex-col flex-1 min-h-0 h-full" : ""}`}
      style={{ backgroundColor: "hsl(var(--muted) / 0.2)" }}
    >
      <div className={`relative w-full ${isFill ? "flex-1 min-h-0" : ""}`} style={isFill ? { height: "100%" } : { height: typeof height === "number" ? height : undefined }}>
        <svg
          viewBox="0 0 500 320"
          width="500"
          height="320"
          className="w-full h-full"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Circles with blend mode */}
          {data.circles.map((circle, i) => {
            const pos = circlePositions[i];
            return (
              <circle
                key={circle.id}
                cx={pos.cx}
                cy={pos.cy}
                r={pos.r}
                fill={circle.color}
                opacity={0.55}
                stroke={circle.color}
                strokeWidth={2}
                style={{ mixBlendMode: "multiply", transition: "opacity 0.2s ease-out" }}
              />
            );
          })}

          {/* Count badges — only single-set exclusives + full intersection */}
          {regions
            .filter((r) => data.circles.length === 2 || r.sets.length === 1 || r.sets.length === data.circles.length)
            .map((region) => {
              const pos = regionPositions[region.key];
              if (!pos || region.items.length === 0) return null;
              const isHovered = hoveredRegion === region.key;
              return (
                <g key={region.key}>
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={region.sets.length === 1 ? 30 : 22}
                    fill="transparent"
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => handleRegionHover(region.key, e)}
                    onMouseMove={(e) => handleRegionHover(region.key, e)}
                    onMouseLeave={() => handleRegionHover(null)}
                  />
                  <circle
                    cx={pos.x}
                    cy={pos.y}
                    r={14}
                    fill={isHovered ? "hsl(var(--foreground))" : "hsl(var(--card))"}
                    stroke="hsl(var(--border))"
                    strokeWidth={1}
                    pointerEvents="none"
                    style={{ transition: "fill 0.2s ease-out" }}
                  />
                  <text
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill={isHovered ? "hsl(var(--card))" : "hsl(var(--foreground))"}
                    pointerEvents="none"
                    style={{ transition: "fill 0.2s ease-out", fontSize: "12px", fontWeight: 600 }}
                  >
                    {region.items.length}
                  </text>
                </g>
              );
            })}

          {/* Circle labels — positioned clearly outside each circle */}
          {data.circles.map((circle, i) => {
            const pos = circlePositions[i];
            const n = data.circles.length;
            let lx: number;
            let ly: number;
            let anchor: string = "middle";

            if (n === 2) {
              // 2 circles: labels above each circle, spread apart
              lx = pos.cx;
              ly = pos.cy - pos.r - 14;
            } else {
              // 3 circles: top-left label goes top-left, top-right goes top-right, bottom goes below
              if (i === 0) {
                // Top-left circle — label to the left
                lx = pos.cx - pos.r - 4;
                ly = pos.cy - pos.r + 10;
                anchor = "end";
              } else if (i === 1) {
                // Top-right circle — label to the right
                lx = pos.cx + pos.r + 4;
                ly = pos.cy - pos.r + 10;
                anchor = "start";
              } else {
                // Bottom circle — label below
                lx = pos.cx;
                ly = pos.cy + pos.r + 20;
                anchor = "middle";
              }
            }

            return (
              <text
                key={`label-${circle.id}`}
                x={lx}
                y={ly}
                textAnchor={anchor}
                fill={circle.color}
                style={{ fontSize: "13px", fontWeight: 600 }}
              >
                {circle.label}
              </text>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoveredRegion && tooltipPos && hoveredItems.length > 0 && (
          <div
            className="absolute pointer-events-none z-10 rounded-md border border-border bg-card px-3 py-2 shadow-sm"
            style={{
              left: tooltipPos.x,
              top: tooltipPos.y,
              transform: "translate(-50%, -100%)",
              maxWidth: 220,
            }}
          >
            {hoveredItems.map((item, i) => (
              <p key={i} className="text-xs text-foreground leading-relaxed">
                • {item}
              </p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
